import type {
  ExpertsBillableClient,
  ExpertsBillableSessionsResponse,
} from '../../services/api';

/**
 * Panneau récap des sessions CRM facturables (Conseil A-facturer) pour un
 * consultant + mois donné. Affiché dans le formulaire de validation Gertrude
 * quand charge_type = "Sous-traitance (experts)".
 *
 * Propose un bouton « Pré-remplir les clients » qui mappe les clients CRM
 * (résolus en customer_books_id via la sync zcrm_account_id) sur les lignes
 * de la facture :
 *  - 1 client + N lignes : applique ce client à toutes les lignes
 *  - N clients + N lignes : applique 1-pour-1 dans l'ordre alphabétique
 *  - autres ratios : désactivé (l'utilisateur pickera manuellement via le
 *    typeahead, la liste reste affichée comme référence)
 */
interface BillableSessionsPanelProps {
  data: ExpertsBillableSessionsResponse;
  lineCount: number;
  onApply: (clientsInOrder: ExpertsBillableClient[]) => void;
}

export function BillableSessionsPanel({ data, lineCount, onApply }: BillableSessionsPanelProps) {
  const clients = data.clients;
  const monthLabel = formatMonth(data.month);

  // Stratégie d'auto-fill : voir le doc en tête du composant.
  let canApply = false;
  let applyClients: ExpertsBillableClient[] = [];
  let applyHint = '';
  const allResolved = clients.length > 0 && clients.every((c) => c.client_books_id);

  if (allResolved && clients.length === 1) {
    canApply = lineCount > 0;
    applyClients = Array(lineCount).fill(clients[0]!);
    applyHint = canApply
      ? `Applique ${clients[0]!.client_name} aux ${lineCount} ligne(s)`
      : '';
  } else if (allResolved && clients.length === lineCount) {
    canApply = true;
    applyClients = clients;
    applyHint = `Applique 1 client par ligne dans l'ordre alphabétique`;
  } else if (allResolved && clients.length > 0) {
    applyHint = `${clients.length} client(s) CRM vs ${lineCount} ligne(s) : assigne manuellement chaque ligne avec le picker.`;
  } else if (!allResolved && clients.length > 0) {
    applyHint = 'Certains clients CRM n\'ont pas de customer Books correspondant (sync à vérifier).';
  }

  if (data.warning) {
    return (
      <div className="mb-2 text-xs rounded-md px-3 py-1.5 ring-1 ring-inset bg-amber-50 ring-amber-200 text-amber-800">
        ⚠️ {data.warning}
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="mb-2 text-xs rounded-md px-3 py-1.5 ring-1 ring-inset bg-slate-50 ring-slate-200 text-slate-600">
        🔗 Aucune session Conseil « A facturer » trouvée pour{' '}
        {data.consultant?.name ?? 'ce consultant'} en {monthLabel}. Tu peux quand même
        saisir manuellement les clients (la sync CRM est peut-être incomplète, ou les
        sessions de ce mois ne sont pas encore classées).
      </div>
    );
  }

  return (
    <div className="mb-2 rounded-md ring-1 ring-inset ring-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          🔗 <strong>{data.consultant?.name}</strong> a{' '}
          <strong>{clients.length}</strong> client(s) avec sessions Conseil A-facturer en{' '}
          {monthLabel} (total{' '}
          <strong>{clients.reduce((s, c) => s + c.hours, 0).toFixed(2)} h</strong>) :
        </div>
        {canApply && (
          <button
            type="button"
            onClick={() => onApply(applyClients)}
            className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
            title={applyHint}
          >
            🪄 Pré-remplir les clients
          </button>
        )}
      </div>
      <ul className="mt-1.5 space-y-0.5 pl-4">
        {clients.map((c) => (
          <li key={c.crm_account_id} className="flex items-baseline gap-2">
            <span className="font-medium">{c.client_name}</span>
            <span className="tabular-nums opacity-80">
              ({c.hours.toFixed(2)} h, {c.session_count} session{c.session_count > 1 ? 's' : ''})
            </span>
            {!c.client_books_id && (
              <span className="text-amber-700">⚠️ pas de customer Books</span>
            )}
          </li>
        ))}
      </ul>
      {applyHint && !canApply && (
        <div className="mt-1.5 text-[11px] opacity-80">{applyHint}</div>
      )}
    </div>
  );
}

function formatMonth(month: string): string {
  // "2026-05" → "mai 2026"
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  const monthNames = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
  ];
  return `${monthNames[m - 1]} ${y}`;
}
